import React, { useContext, useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import { Mission } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { Address } from "../address/address";
import ListItem from "../../ui_atoms/ListItem";
import AddressField from "../address/AddressField";
import { Buildable } from "ts-essentials";
import Gap from "../../ui_atoms/Gap";
import { BodyMediumText } from "../FormattedText";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { useTheme } from "../../hooks/use-theme";
import FullWidthLine from "../FullWidthLine";

type MissionWorkingAddressProps = {
    mission: Buildable<Mission>;
    setWorkingAddress: (address: Address | null) => void;
};

const MissionWorkingAddress: React.FC<MissionWorkingAddressProps> = ({
    mission,
    setWorkingAddress,
}) => {
    const i18n = useI18n();
    const { gridSize, colors } = useTheme();
    const currentProfile = useCurrentProfile();
    const { user } = useContext(AuthManagerContext);

    const isPrivateClient = currentProfile?.isPrivateClient || false;

    const clientDefaultAddress = useMemo(() => {
        const titleKey = isPrivateClient
            ? "screens.user.create_mission_working_address.private_client_address_title"
            : "screens.user.create_mission_working_address.client_address_title";
        if (currentProfile?.client?.address) {
            return {
                title: i18n.t(titleKey),
                description: currentProfile.client.address.formatted,
                onPress: () => setWorkingAddress(currentProfile.client.address as Address),
            };
        } else if (user?.address) {
            return {
                title: i18n.t(titleKey),
                description: user.address.formatted,
                onPress: () => setWorkingAddress(user.address as Address),
            };
        }
    }, [currentProfile?.client?.address, i18n, isPrivateClient, setWorkingAddress, user?.address]);

    const listItemStyle = {
        marginHorizontal: -(gridSize / 3),
    };

    return (
        <VStack>
            <BodyMediumText>
                {isPrivateClient
                    ? i18n.t(
                          "screens.user.create_mission_working_address.private_client_description_top",
                      )
                    : i18n.t("screens.user.create_mission_working_address.description_top")}
            </BodyMediumText>
            <Gap gap={1} />
            {clientDefaultAddress && (
                <ListItem
                    testID="client-default-address"
                    selectable
                    checked={mission.currentContract?.useClientWorkingAddress || false}
                    inGroup
                    style={listItemStyle}
                    {...clientDefaultAddress}
                />
            )}
            {mission.currentContract?.workingAddress &&
                !mission.currentContract?.useClientWorkingAddress && (
                    <ListItem
                        selectable
                        title={i18n.t("screens.user.create_mission_working_address.other_address")}
                        description={mission.currentContract.workingAddress.formatted}
                        checked={true}
                        onPress={() => {}}
                        style={listItemStyle}
                        inGroup
                    />
                )}
            <FullWidthLine color={colors.accentLighter} />
            <AddressField
                asListAction="primary"
                style={[
                    listItemStyle,
                    {
                        borderBottomWidth: 0,
                    },
                ]}
                value={undefined}
                label={i18n.t("screens.user.create_mission_working_address.add_address")}
                onSelect={(address) => {
                    setWorkingAddress(address);
                }}
                inputPlaceholder="Saisisez l'adresse de la mission"
                right="write"
                allowedCountries={["ch"]}
            />
        </VStack>
    );
};

export default MissionWorkingAddress;
